import type {
  AnalyticsEvent,
  DiscoveryFacts,
  Evidence,
  PlaybookFacts,
  ExportDraft,
  ExportRecord,
  HistoryEvent,
  ObjectiveEvent,
  ObjectiveState,
  RecommendationLink,
  Snapshot,
  SyncRun,
} from '@/api/types/readiness'
import type { LocalPlaybookAction } from '@/lib/readiness/facts'

// ── Scope shape ─────────────────────────────────────────────────────────────
// Everything the readiness engine keeps per org × index. The record shapes
// mirror the tables in Engineering Note 2. Kept apart from the store so a
// scope can be built without importing it.

export interface ScopeSettings {
  snapshot_rule: 'quarterly' | 'monthly'
  sync_time: string // HH:mm local
  discovery_facts: DiscoveryFacts | null // last facts seen by sync (for drafts and Part B)
  // Last Playbook facts seen by sync (Issuing CAs, NIST remediation); absent on
  // scopes persisted before the Playbook read model existed.
  playbook_facts?: PlaybookFacts | null
  // Iteration 3 Note 3: growth in known assets that emits `scope_expanded`
  // (default 5% or 50 assets, whichever is smaller).
  scope_threshold?: { pct: number; abs: number }
}

// Iteration 3 §12: the option chosen on a vendor-gated step.
export interface StepChoice {
  option: 'wait' | 'pilot' | 'waiver'
  at: string
  by: string
  waiver_expiry?: string | null
  waiver_ref?: string | null
}

export const stepKey = (code: string, transition: string, bullet: number) => `${code}|${transition}|${bullet}`
export const DEFAULT_SCOPE_THRESHOLD = { pct: 5, abs: 50 }

export interface ScopeData {
  states: Record<string, ObjectiveState>
  evidence: Evidence[]
  // Append-only objective event log (Iteration 2 §2), oldest first.
  events: ObjectiveEvent[]
  snapshots: Snapshot[]
  sync_runs: SyncRun[]
  links: RecommendationLink[]
  history: HistoryEvent[]
  drafts: ExportDraft[]
  exports: ExportRecord[]
  playbook_actions: LocalPlaybookAction[]
  analytics: AnalyticsEvent[]
  settings: ScopeSettings
  next_scheduled_snapshot: string | null
  step_choices?: Record<string, StepChoice>
}

export const emptyScope = (): ScopeData => ({
  states: {},
  evidence: [],
  events: [],
  snapshots: [],
  sync_runs: [],
  links: [],
  history: [],
  drafts: [],
  exports: [],
  playbook_actions: [],
  analytics: [],
  settings: { snapshot_rule: 'quarterly', sync_time: '05:00', discovery_facts: null },
  next_scheduled_snapshot: null,
})

export const scopeKey = (orgId: string, indexId: string) => `${orgId || 'anon'}::${indexId}`

export function emptyState(orgId: string, indexId: string, code: string): ObjectiveState {
  return {
    org_id: orgId,
    index_id: indexId,
    objective_code: code,
    level: 'L0',
    status: 'unset',
    set_by: null,
    set_at: null,
    note: null,
    suggested_level: null,
    suggested_at: null,
    suggested_reason: null,
    owner: null,
    target_level_text: null,
    due_at: null,
    basis: 'internal',
    score: null,
    tier: null,
    suggested_score: null,
    suggested_tier: null,
  }
}

// Quarterly on the 15th of Mar/Jun/Sep/Dec; monthly on the 15th. A reminder to
// save an assessment, never a constraint on when levels change (I2 §1.2, OQ 7).
export const SNAPSHOT_MONTHS: Record<ScopeSettings['snapshot_rule'], number[]> = {
  quarterly: [2, 5, 8, 11],
  monthly: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
}

export function nextScheduledSnapshot(rule: ScopeSettings['snapshot_rule'], from = new Date()): string {
  const months = SNAPSHOT_MONTHS[rule]
  for (let i = 0; i < 24; i++) {
    const d = new Date(from.getFullYear(), from.getMonth() + i, 15, 9, 0, 0)
    if (d > from && months.includes(d.getMonth())) return d.toISOString()
  }
  return from.toISOString()
}

// The last `count` scheduled snapshot dates at or before `from`, oldest first.
export function pastScheduledSnapshots(rule: ScopeSettings['snapshot_rule'], count: number, from = new Date()): Date[] {
  const months = SNAPSHOT_MONTHS[rule]
  const out: Date[] = []
  for (let i = 0; i < 48 && out.length < count; i++) {
    const d = new Date(from.getFullYear(), from.getMonth() - i, 15, 9, 0, 0)
    if (d <= from && months.includes(d.getMonth())) out.push(d)
  }
  return out.reverse()
}
